import { useState } from "react";
import { Card, Table, Tag, Select, Input, Popconfirm, message, Statistic, Row, Col } from "antd";
import { Smartphone } from "lucide-react";
import { usePushTokens, usePushTokenStats, usePushTokenDeactivate } from "../../../api-hooks/pushTokens";
import { TPushNotificationToken } from "../../../types";
import type { ColumnsType } from "antd/es/table";
import MyPagination from "../../components/Pagination";
import type { QData } from "../../../types/basic-type-list";

const platformColor: Record<string, string> = { ios: "blue", android: "green", web: "orange" };

export default function PushTokens() {
  const [messageApi, ctx] = message.useMessage();
  const [companyId, setCompanyId] = useState<string>("");
  const [platform, setPlatform] = useState<string>("");
  const [isActive, setIsActive] = useState<string>("");
  const [qData, setQData] = useState<QData>({ page: 1, limit: 50 });

  const { data: result, isLoading } = usePushTokens({
    company_id: companyId || undefined,
    platform: platform || undefined,
    is_active: isActive || undefined,
    page: qData.page,
    limit: qData.limit,
  });

  const { data: stats } = usePushTokenStats();

  const { mutate: deactivate } = usePushTokenDeactivate({
    onSuccess: () => messageApi.success("Token deactivated."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to deactivate."),
  });

  const columns: ColumnsType<TPushNotificationToken> = [
    {
      title: "User",
      key: "user",
      render: (_: any, r: TPushNotificationToken) => (
        <div>
          <div className="text-sm text-slate-700">{r.user_name}</div>
          <div className="text-xs text-slate-400">{r.user_email}</div>
        </div>
      ),
    },
    { title: "Company", dataIndex: "company_name", key: "company_name", render: (v: string | null) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (v: string) => <Tag color={platformColor[v] || "default"}><Smartphone size={10} className="inline mr-1" />{v}</Tag>,
    },
    { title: "Device ID", dataIndex: "device_id", key: "device_id", render: (v: string | null) => <span className="font-mono text-xs text-slate-400">{v ? v.substring(0, 16) + "…" : "—"}</span> },
    { title: "App Version", dataIndex: "app_version", key: "app_version", render: (v: string | null) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Last Used",
      dataIndex: "last_used_at",
      key: "last_used_at",
      render: (v: string | null) => v ? <span className="text-xs text-slate-400">{new Date(v).toLocaleDateString("en-GB")}</span> : <span className="text-slate-300">—</span>,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => <span className="text-xs text-slate-400">{new Date(v).toLocaleDateString("en-GB")}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TPushNotificationToken) =>
        record.is_active ? (
          <Popconfirm
            title="Deactivate this token?"
            description="Device will no longer receive push notifications."
            onConfirm={() => deactivate(record.id)}
            okText="Deactivate"
            okButtonProps={{ danger: true }}
          >
            <button className="text-xs text-red-500 hover:underline cursor-pointer bg-transparent border-0">Deactivate</button>
          </Popconfirm>
        ) : (
          <span className="text-xs text-slate-300">Inactive</span>
        ),
    },
  ];

  return (
    <div>
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Push Notification Tokens</h2>
          <p className="text-slate-500 text-sm mt-0.5">Device tokens registered for push notifications across all companies.</p>
        </div>
      </div>

      {stats && (
        <Row gutter={16} className="mb-6">
          <Col span={8}>
            <Card className="rounded-2xl border border-slate-200 shadow-sm">
              <Statistic title="Active Tokens" value={stats.active} valueStyle={{ color: "#16a34a" }} />
            </Card>
          </Col>
          <Col span={8}>
            <Card className="rounded-2xl border border-slate-200 shadow-sm">
              <Statistic title="Inactive Tokens" value={stats.inactive} valueStyle={{ color: "#94a3b8" }} />
            </Card>
          </Col>
          <Col span={8}>
            <Card className="rounded-2xl border border-slate-200 shadow-sm">
              <div className="text-sm text-slate-500 mb-2">By Platform (Active)</div>
              {stats.by_platform.map((p) => (
                <Tag key={p.platform} color={platformColor[p.platform] || "default"} className="mr-1">
                  {p.platform}: {p.total}
                </Tag>
              ))}
            </Card>
          </Col>
        </Row>
      )}

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4" styles={{ body: { padding: "12px 16px" } }}>
        <div className="flex flex-wrap gap-3 items-end">
          <Input
            placeholder="Company ID"
            style={{ width: 140 }}
            value={companyId}
            onChange={(e) => { setCompanyId(e.target.value); setQData(q => ({ ...q, page: 1 })); }}
            allowClear
          />
          <Select
            placeholder="Platform"
            style={{ width: 130 }}
            allowClear
            value={platform || undefined}
            onChange={(v) => { setPlatform(v || ""); setQData(q => ({ ...q, page: 1 })); }}
            options={[
              { label: "iOS", value: "ios" },
              { label: "Android", value: "android" },
              { label: "Web", value: "web" },
            ]}
          />
          <Select
            placeholder="Status"
            style={{ width: 120 }}
            allowClear
            value={isActive || undefined}
            onChange={(v) => { setIsActive(v || ""); setQData(q => ({ ...q, page: 1 })); }}
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
          />
        </div>
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={result?.data ?? []}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          size="middle"
          scroll={{ x: 1100 }}
          locale={{ emptyText: "No push tokens found." }}
        />
      </Card>

      {(result?.total ?? 0) > (qData.limit ?? 50) && (
        <div className="mt-4 flex justify-end">
          <MyPagination
            qData={qData}
            total={result?.total ?? 0}
            onChange={(newQData) => setQData(newQData)}
          />
        </div>
      )}
    </div>
  );
}
